/**
 * Which drawing library is in use, provided rather than imported.
 *
 * The indirection is the point. If `ReportView` imported an adapter directly,
 * that adapter's library would be a hard dependency of the whole package and
 * swapping it would mean editing every chart site. Provided, the choice is one
 * line in the host — and a host that draws no charts installs no chart
 * library, which is why `recharts` is a PEER dependency here.
 *
 * There is no default adapter on purpose. A silent fallback to some built-in
 * renderer would let a host forget the provider and ship a product whose
 * charts quietly differ from the ones under test.
 */
import { createContext, type ReactElement, type ReactNode, useContext } from 'react';
import type { ChartSpec } from './chart-spec';

/**
 * Draws a {@link ChartSpec}. Implemented once per charting library.
 *
 * The caller guarantees a parent with a real width and height (`.rk-chart`),
 * so an adapter that lays out by measuring its container has something to
 * measure. It must not set its own page-level sizing.
 */
export type ChartAdapter = (spec: ChartSpec) => ReactElement | null;

const ChartAdapterContext = createContext<ChartAdapter | null>(null);

export function ChartAdapterProvider({
  adapter,
  children,
}: {
  adapter: ChartAdapter;
  children: ReactNode;
}) {
  return <ChartAdapterContext.Provider value={adapter}>{children}</ChartAdapterContext.Provider>;
}

/**
 * The adapter in force, or `null` when the host provided none.
 *
 * Returns null rather than throwing: a missing provider should degrade to the
 * table the reader can still read, not blank the page. The caller says so.
 */
export function useChartAdapter(): ChartAdapter | null {
  return useContext(ChartAdapterContext);
}
